import React, { useState, useEffect } from 'react';
import { 
  Tabs, Tab, Box, Paper, Typography, Button, TextField, IconButton, 
  List, ListItem, ListItemText, ListItemSecondaryAction, Dialog, 
  DialogTitle, DialogContent, DialogActions, DialogContentText,
  Tooltip, Divider, Chip
} from '@mui/material';
import { 
  FaUserShield, FaChalkboardTeacher, FaPlus, FaTrash, 
  FaEdit, FaExclamationTriangle, FaEnvelope, FaUserCog,
  FaUserGraduate, FaTools, FaUserEdit, FaClipboardCheck,
  FaMoneyBillWave, FaPaintBrush, FaHeadset
} from 'react-icons/fa';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import './admins_tutors.css';

// # AdminsTutors component for managing admin, tutor and reviewer emails
const AdminsTutors = () => {
  // # State management for tabs, emails, and dialog
  const [tabValue, setTabValue] = useState(0);
  const [adminEmails, setAdminEmails] = useState([]);
  const [tutorEmails, setTutorEmails] = useState([]);
  const [reviewerEmails, setReviewerEmails] = useState([]); // # Add reviewer emails state
  const [financialEmails, setFinancialEmails] = useState([]); // # Add financial emails state
  const [designerEmails, setDesignerEmails] = useState([]); // # Add designer emails state
  const [supportEmails, setSupportEmails] = useState([]); // # Add support emails state
  const [newEmail, setNewEmail] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // # Fetch admin, tutor and reviewer emails on component mount
  useEffect(() => {
    const fetchEmails = async () => {
      try {
        // # Fetch all emails from single document
        const emailsDoc = await getDoc(doc(db, 'administration', 'emails'));
        if (emailsDoc.exists()) {
          const data = emailsDoc.data();
          setAdminEmails(data.admins || []);
          setTutorEmails(data.tutors || []);
          setReviewerEmails(data.reviewers || []); // # Set reviewer emails
          setFinancialEmails(data.financial || []); // # Set financial emails
          setDesignerEmails(data.designers || []); // # Set designer emails
          setSupportEmails(data.support || []); // # Set support emails
        }
      } catch (error) {
        console.error('Error fetching emails:', error);
      }
    };

    fetchEmails();
  }, []);

  // # Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setNewEmail('');
    setEmailError('');
  };

  // # Validate email format
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // # Get current emails array based on active tab
  const getCurrentEmails = () => {
    switch (tabValue) {
      case 0:
        return adminEmails;
      case 1:
        return tutorEmails;
      case 2:
        return reviewerEmails;
      case 3:
        return financialEmails;
      case 4:
        return designerEmails;
      case 5:
        return supportEmails;
      default:
        return [];
    }
  };

  // # Get current role name based on active tab
  const getCurrentRole = () => {
    switch (tabValue) {
      case 0:
        return 'admin';
      case 1:
        return 'tutor';
      case 2:
        return 'reviewer';
      case 3:
        return 'financial';
      case 4:
        return 'designer';
      case 5:
        return 'support';
      default:
        return '';
    }
  };

  // # Get role display name
  const getRoleDisplayName = () => {
    switch (tabValue) {
      case 0:
        return 'Admin';
      case 1:
        return 'Tutor';
      case 2:
        return 'Content Reviewer';
      case 3:
        return 'Financial Manager';
      case 4:
        return 'Designer';
      case 5:
        return 'Support Staff';
      default:
        return '';
    }
  };

  // # Get role icon based on active tab
  const getRoleIcon = () => {
    switch (tabValue) {
      case 0:
        return <FaUserCog size={18} />;
      case 1:
        return <FaUserGraduate size={18} />;
      case 2:
        return <FaClipboardCheck size={18} />;
      case 3:
        return <FaMoneyBillWave size={18} />;
      case 4:
        return <FaPaintBrush size={18} />;
      case 5:
        return <FaHeadset size={18} />;
      default:
        return null;
    }
  };

  // # Handle adding new email
  const handleAddEmail = async () => {
    if (!validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const currentEmails = getCurrentEmails();
    if (currentEmails.includes(newEmail)) {
      setEmailError('Email already exists');
      return;
    }

    try {
      const emailsDoc = await getDoc(doc(db, 'administration', 'emails'));
      const existingData = emailsDoc.exists() ? emailsDoc.data() : { 
        admins: [], 
        tutors: [], 
        reviewers: [],
        financial: [],
        designers: [],
        support: []
      };
      
      // # Ensure all required fields exist in the document
      if (!existingData.admins) existingData.admins = [];
      if (!existingData.tutors) existingData.tutors = [];
      if (!existingData.reviewers) existingData.reviewers = [];
      if (!existingData.financial) existingData.financial = [];
      if (!existingData.designers) existingData.designers = [];
      if (!existingData.support) existingData.support = [];
      
      // # Determine which array to update based on active tab
      let arrayField;
      switch (tabValue) {
        case 0:
          arrayField = 'admins';
          break;
        case 1:
          arrayField = 'tutors';
          break;
        case 2:
          arrayField = 'reviewers';
          break;
        case 3:
          arrayField = 'financial';
          break;
        case 4:
          arrayField = 'designers';
          break;
        case 5:
          arrayField = 'support';
          break;
        default:
          arrayField = '';
      }
      
      const updatedData = {
        ...existingData,
        [arrayField]: [...(existingData[arrayField] || []), newEmail]
      };
      
      await setDoc(doc(db, 'administration', 'emails'), updatedData);
      
      // # Update appropriate state based on active tab
      switch (tabValue) {
        case 0:
          setAdminEmails(updatedData.admins);
          break;
        case 1:
          setTutorEmails(updatedData.tutors);
          break;
        case 2:
          setReviewerEmails(updatedData.reviewers);
          break;
        case 3:
          setFinancialEmails(updatedData.financial);
          break;
        case 4:
          setDesignerEmails(updatedData.designers);
          break;
        case 5:
          setSupportEmails(updatedData.support);
          break;
      }
      
      setNewEmail('');
      setEmailError('');
      setOpenDialog(false);
    } catch (error) {
      console.error('Error adding email:', error);
      setEmailError('Failed to add email. Please try again.');
    }
  };

  // # Handle removing email
  const handleRemoveEmail = async (emailToRemove) => {
    let arrayField;
    switch (tabValue) {
      case 0:
        arrayField = 'admins';
        break;
      case 1:
        arrayField = 'tutors';
        break;
      case 2:
        arrayField = 'reviewers';
        break;
      case 3:
        arrayField = 'financial';
        break;
      case 4:
        arrayField = 'designers';
        break;
      case 5:
        arrayField = 'support';
        break;
      default:
        arrayField = '';
    }
    
    const currentEmails = getCurrentEmails();
    
    try {
      const emailsDoc = await getDoc(doc(db, 'administration', 'emails'));
      const existingData = emailsDoc.exists() ? emailsDoc.data() : { 
        admins: [], 
        tutors: [], 
        reviewers: [],
        financial: [],
        designers: [],
        support: []
      };
      
      // # Ensure all required fields exist in the document
      if (!existingData.admins) existingData.admins = [];
      if (!existingData.tutors) existingData.tutors = [];
      if (!existingData.reviewers) existingData.reviewers = [];
      if (!existingData.financial) existingData.financial = [];
      if (!existingData.designers) existingData.designers = [];
      if (!existingData.support) existingData.support = [];
      
      const updatedData = {
        ...existingData,
        [arrayField]: currentEmails.filter(email => email !== emailToRemove)
      };
      
      await setDoc(doc(db, 'administration', 'emails'), updatedData);
      
      // # Update appropriate state based on active tab
      switch (tabValue) {
        case 0:
          setAdminEmails(updatedData.admins);
          break;
        case 1:
          setTutorEmails(updatedData.tutors);
          break;
        case 2:
          setReviewerEmails(updatedData.reviewers);
          break;
        case 3:
          setFinancialEmails(updatedData.financial);
          break;
        case 4:
          setDesignerEmails(updatedData.designers);
          break;
        case 5:
          setSupportEmails(updatedData.support);
          break;
      }
    } catch (error) {
      console.error('Error removing email:', error);
    }
  };

  // # Handle edit email dialog
  const handleEditClick = (email) => {
    setEditEmail(email);
    setNewEmail(email);
    setEditDialogOpen(true);
  };

  // # Handle edit email submit
  const handleEditSubmit = async () => {
    if (!validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const currentEmails = getCurrentEmails();
    if (newEmail !== editEmail && currentEmails.includes(newEmail)) {
      setEmailError('Email already exists');
      return;
    }

    try {
      const emailsDoc = await getDoc(doc(db, 'administration', 'emails'));
      const existingData = emailsDoc.exists() ? emailsDoc.data() : { 
        admins: [], 
        tutors: [], 
        reviewers: [],
        financial: [],
        designers: [],
        support: []
      };
      
      // # Ensure all required fields exist in the document
      if (!existingData.admins) existingData.admins = [];
      if (!existingData.tutors) existingData.tutors = [];
      if (!existingData.reviewers) existingData.reviewers = [];
      if (!existingData.financial) existingData.financial = [];
      if (!existingData.designers) existingData.designers = [];
      if (!existingData.support) existingData.support = [];
      
      const updatedEmails = currentEmails.map(email => 
        email === editEmail ? newEmail : email
      );
      
      let arrayField;
      switch (tabValue) {
        case 0:
          arrayField = 'admins';
          break;
        case 1:
          arrayField = 'tutors';
          break;
        case 2:
          arrayField = 'reviewers';
          break;
        case 3:
          arrayField = 'financial';
          break;
        case 4:
          arrayField = 'designers';
          break;
        case 5:
          arrayField = 'support';
          break;
        default:
          arrayField = '';
      }
      
      const updatedData = {
        ...existingData,
        [arrayField]: updatedEmails
      };
      
      await setDoc(doc(db, 'administration', 'emails'), updatedData);
      
      // # Update appropriate state based on active tab
      switch (tabValue) {
        case 0:
          setAdminEmails(updatedData.admins);
          break;
        case 1:
          setTutorEmails(updatedData.tutors);
          break;
        case 2:
          setReviewerEmails(updatedData.reviewers);
          break;
        case 3:
          setFinancialEmails(updatedData.financial);
          break;
        case 4:
          setDesignerEmails(updatedData.designers);
          break;
        case 5:
          setSupportEmails(updatedData.support);
          break;
      }
      
      setNewEmail('');
      setEditEmail('');
      setEmailError('');
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error editing email:', error);
      setEmailError('Failed to edit email. Please try again.');
    }
  };

  // # Handle delete click
  const handleDeleteClick = (email) => {
    setDeleteEmail(email);
    setDeleteDialogOpen(true);
  };

  // # Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      await handleRemoveEmail(deleteEmail);
      setDeleteDialogOpen(false);
      setDeleteEmail('');
    } catch (error) {
      console.error('Error confirming delete:', error);
    }
  };

  return (
    <div className="admins_tutors_container">
      <SharedHeader title="User Management" />
      
      <div className="admins_tutors_content">
        <Paper className="admins_tutors_paper">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            className="admins_tutors_tabs"
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: {
                height: '3px'
              }
            }}
          >
            <Tab 
              icon={<FaUserShield size={20} />}
              iconPosition="start"
              label="Admins" 
              className="admins_tutors_tab"
            />
            <Tab 
              icon={<FaChalkboardTeacher size={20} />}
              iconPosition="start"
              label="Tutors" 
              className="admins_tutors_tab"
            />
            <Tab 
              icon={<FaUserEdit size={20} />}
              iconPosition="start"
              label="Content Reviewers" 
              className="admins_tutors_tab"
            />
            <Tab 
              icon={<FaMoneyBillWave size={20} />}
              iconPosition="start"
              label="Financial Managers" 
              className="admins_tutors_tab"
            />
            <Tab 
              icon={<FaPaintBrush size={20} />}
              iconPosition="start"
              label="Designers" 
              className="admins_tutors_tab"
            />
            <Tab 
              icon={<FaHeadset size={20} />}
              iconPosition="start"
              label="Support" 
              className="admins_tutors_tab"
            />
          </Tabs>

          <Box className="admins_tutors_tab_content">
            <div className="admins_tutors_section">
              <div className="admins_tutors_section_header">
                <Typography variant="h6">
                  <FaEnvelope size={18} style={{ marginRight: '8px' }} /> {getRoleDisplayName()} Emails
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FaPlus size={16} />}
                  onClick={() => setOpenDialog(true)}
                >
                  Add {getRoleDisplayName()}
                </Button>
              </div>

              <List className="admins_tutors_list">
                {getCurrentEmails() && getCurrentEmails().length > 0 ? (
                  getCurrentEmails().map((email) => (
                    <ListItem 
                      key={email} 
                      className="admins_tutors_list_item"
                      disablePadding={false}
                      sx={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between' }}
                    >
                      <ListItemText 
                        primary={email}
                        primaryTypographyProps={{
                          style: { 
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                      <div className="admins_tutors_actions">
                        <Tooltip title="Edit Email" arrow placement="top">
                          <IconButton
                            aria-label="edit"
                            onClick={() => handleEditClick(email)}
                            className="admins_tutors_edit_btn"
                            size="small"
                          >
                            <FaEdit size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove Email" arrow placement="top">
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleDeleteClick(email)}
                            className="admins_tutors_delete_btn"
                            size="small"
                          >
                            <FaTrash size={14} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </ListItem>
                  ))
                ) : (
                  <ListItem className="admins_tutors_empty_list">
                    <ListItemText 
                      primary={`No ${getRoleDisplayName()} emails found`}
                      primaryTypographyProps={{
                        style: { color: '#94a3b8', textAlign: 'center' }
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </div>

            <div className="admins_tutors_section">
              <div className="admins_tutors_section_header">
                <Typography variant="h6">
                  {getRoleIcon()}
                  <span style={{ marginLeft: '8px' }}>{getRoleDisplayName()} Management</span>
                </Typography>
                <Chip 
                  icon={<FaTools size={14} />} 
                  label="Coming Soon" 
                  variant="outlined" 
                  color="primary"
                  size="small"
                />
              </div>
              <Typography variant="h6" className="admins_tutors_coming_soon">
                Additional management features coming soon...
              </Typography>
            </div>
          </Box>
        </Paper>
      </div>

      {/* Add Email Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        className="admins_tutors_dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="admins_tutors_dialog_title">
          Add New {getRoleDisplayName()} Email
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddEmail} 
            variant="contained" 
            color="primary"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => {
          setEditDialogOpen(false);
          setNewEmail('');
          setEmailError('');
        }}
        className="admins_tutors_dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="admins_tutors_dialog_title">
          <FaEdit size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Edit {getRoleDisplayName()} Email
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: 'text.secondary' }}>
            Update the email address below. This will update all associated permissions.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setNewEmail('');
              setEmailError('');
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        className="admins_tutors_dialog admins_tutors_delete_dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="admins_tutors_dialog_title admins_tutors_delete_title">
          <FaExclamationTriangle size={20} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#dc2626' }} />
          Confirm Removal
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: 'text.secondary' }}>
            Are you sure you want to remove this {getRoleDisplayName()}?
            <br />
            <strong>{deleteEmail}</strong>
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<FaTrash size={14} />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      
      <DashboardFooter />
    </div>
  );
};

export default AdminsTutors;
